/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Button, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { retrieveCardPaymentAcceptResultAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import classNames from 'classnames';
import * as React from 'react';

import { CardPaymentAcceptPoint } from '@msdyn365-commerce/retail-proxy';
import { ICheckoutApplePayData } from './checkout-apple-pay.data';
import { ICheckoutApplePayProps } from './checkout-apple-pay.props.autogenerated';

interface ICheckoutApplePayState {
    paymentConnectorId?: string;
}

interface IValidationEvent {
    validationUrl: string;
}

interface IAppleSessionHandler {
    onValidation(resolve: (result: string) => void, reject: () => void, validationEvent: IValidationEvent): void;
    onAuthorize(): void;
    onCancel(): void;
    onPaymentMethodUpdate?(): void;
    onShippingMethodUpdate?(): void;
}

export interface ICheckoutApplePayViewProps extends ICheckoutApplePayProps<{}>, ICheckoutApplePayState {
    className?: string;
    checkoutApplyPay: IModuleProps;
    applePayButton?: React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any;

/**
 *
 * CheckoutApplePay component
 * @extends {React.PureComponent<ICheckoutApplePayProps<ICheckoutApplePayData>>}
 */
export class CheckoutApplePay extends React.PureComponent<ICheckoutApplePayProps<ICheckoutApplePayData>, ICheckoutApplePayData> {
    private readonly moduleClassName: string = 'ms-checkout-apple-pay';
    private readonly applePaySdkId = 'd365-apple-pay-sdk';

    public constructor(props: ICheckoutApplePayProps<ICheckoutApplePayData>) {
        super(props);
    }

    public componentDidMount() {
        // load JS portion of payment connector for ApplePay
        this.props.data.cardPaymentAcceptPoint?.then(res => this.loadPaymentConnectorSdk(res));

        // load official ApplePay JS SDK.
        const script = document.createElement('script');
        script.src = 'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js';
        script.async = false;
        script.onerror = () => console.log('unable to load apple script');
        document.body.appendChild(script);
    }

    public render(): JSX.Element | null {
        const {
            config: { className }
            // telemetry
        } = this.props;

        const applePayButton = <Button onClick={this.handlePayButton}>Pay with Apple</Button>;
        const viewProps: ICheckoutApplePayViewProps = {
            ...this.props,
            ...this.state,
            className,
            checkoutApplyPay: {
                moduleProps: this.props,
                className: classNames(this.moduleClassName, className)
            },
            applePayButton
        };

        return this.props.renderView(viewProps);
    }

    public handlePayButton = () => {
        console.log('[handlePayButton] pay with apple clicked.');

        const handler: IAppleSessionHandler = {
            onValidation: this.validateMerchant,
            onAuthorize: this.authorizePayment,
            onCancel: this.cancelPayment,
            onPaymentMethodUpdate: this.updatePaymentMethod,
            onShippingMethodUpdate: this.updateShippingMethod
        };

        // calling connector JS to create payment session.
        window.CreateApplePaySession(handler);
    };

    private loadPaymentConnectorSdk = (cardPaymentAcceptPoint: CardPaymentAcceptPoint) => {
        if (!cardPaymentAcceptPoint.AcceptPageContent) {
            this.props.telemetry.error(`Card payment accept page content can't be null or empty`);
        }

        const connectorSdk: string = cardPaymentAcceptPoint.AcceptPageContent || '';
        const sdk = document.createElement('script');
        sdk.id = this.applePaySdkId;
        sdk.type = 'text/javascript';
        sdk.appendChild(document.createTextNode(connectorSdk));
        document.body.appendChild(sdk);
    };

    private validateMerchant = (resolve: (result: string) => void, reject: () => void, validationEvent: IValidationEvent): void => {
        const {
            data: { checkout, cardPaymentAcceptPoint },
            telemetry
        } = this.props;

        console.log(`[validateMerchant] received event: ${JSON.stringify(validationEvent)}`);
        const validationUrl = validationEvent?.validationUrl || '';
        if (!validationUrl) {
            telemetry.error(`Validation Url can't be null or empty`);
            reject();
        }

        const cartId = checkout?.result?.checkoutCart.cart.Id || '';
        if (!cartId) {
            telemetry.error('Card identifier can not be null or empty');
            reject();
        }

        const paymentConnectorId = cardPaymentAcceptPoint?.result?.PaymentConnectorId || '';
        if (!paymentConnectorId) {
            telemetry.error('Connector identifier can not be null or empty');
            reject();
        }

        const settings = {
            PaymentConnectorId: paymentConnectorId
        };

        console.log(`[validationCallback] validationEvent: ${JSON.stringify(validationEvent)}`);
        const validationObject = {
            domainName: window.location.hostname,
            validationUrl: validationUrl
        };
        const validationBody = JSON.stringify(validationObject);
        console.log(`[handlePayButton] validationBody: ${validationBody}`);

        retrieveCardPaymentAcceptResultAsync(
            { callerContext: this.props.context.actionContext },
            Buffer.from(validationBody).toString('base64'),
            [],
            cartId,
            settings
        )
            .then(async cardPaymentAcceptResult => {
                const validationResult: string = JSON.stringify(cardPaymentAcceptResult);
                console.log(`[retrieveCardPaymentAcceptResultAsync] result: ${validationResult}`);
                resolve(validationResult);
            })
            .catch(error => {
                telemetry.error(`retrieveCardPaymentAcceptResultAsync failed, error: ${error}`);
                reject();
            });
    };

    private authorizePayment = (): void => {
        console.log('authorizePayment');
    };

    private cancelPayment = (): void => {
        console.log('cancelPayment');
    };

    private updateShippingMethod = (): void => {
        console.log('updateShippingMethod');
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private updatePaymentMethod = (): void => {
        console.log('updatePaymentMethod');
    };
}

export default CheckoutApplePay;
